import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Info } from "lucide-react";

const ISA_ALLOWANCE = 20000;
const CGT_RATE_BASIC = 0.10;
const CGT_RATE_HIGHER = 0.20;
const DIVIDEND_TAX_BASIC = 0.0875;
const DIVIDEND_TAX_HIGHER = 0.3375;

export default function ISACalculator() {
  const [annual, setAnnual] = useState(10000);
  const [rate, setRate] = useState(7);
  const [years, setYears] = useState(20);
  const [dividendYield, setDividendYield] = useState(2);
  const [taxBand, setTaxBand] = useState("basic");

  const calcGrowth = (contrib) => {
    let total = 0;
    for (let i = 0; i < years; i++) {
      total = (total + contrib) * (1 + rate / 100);
    }
    return total;
  };

  const isaValue = calcGrowth(Math.min(annual, ISA_ALLOWANCE));
  const giaValue = calcGrowth(annual);
  const giaGain = giaValue - annual * years;
  const cgtRate = taxBand === "basic" ? CGT_RATE_BASIC : CGT_RATE_HIGHER;
  const divTaxRate = taxBand === "basic" ? DIVIDEND_TAX_BASIC : DIVIDEND_TAX_HIGHER;
  const giaDividendTax = giaValue * (dividendYield / 100) * divTaxRate;
  const giaCGT = Math.max(0, giaGain - 3000) * cgtRate;
  const giaAfterTax = giaValue - giaCGT - giaDividendTax;
  const taxSaving = giaValue - giaAfterTax;

  const fmt = (n) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);

  return (
    <div className="bg-slate-950 min-h-screen pt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/tools"
          data-testid="back-to-tools"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Tools
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-violet-400" />
          </div>
          <span className="text-violet-400 text-sm font-medium">Tax Efficiency</span>
        </div>
        <h1
          data-testid="isa-calculator-title"
          className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight"
        >
          ISA vs GIA Comparison
        </h1>
        <p className="text-slate-400 mb-10">
          See how much tax you save by investing inside a Stocks & Shares ISA vs a General Investment Account.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Inputs */}
          <div data-testid="isa-inputs" className="lg:col-span-2 bg-slate-900/50 border border-slate-800 rounded-2xl p-6 space-y-5">
            <h2 className="font-outfit font-semibold text-white text-lg">Parameters</h2>

            <div>
              <label className="block text-slate-400 text-sm mb-1.5">Annual Investment</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                <input
                  type="number"
                  data-testid="input-annual"
                  value={annual}
                  onChange={(e) => setAnnual(Number(e.target.value))}
                  className="w-full bg-slate-950 border border-slate-700 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/30 rounded-lg pl-7 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                />
              </div>
              {annual > ISA_ALLOWANCE && (
                <p className="text-amber-400 text-xs mt-1">ISA capped at £{ISA_ALLOWANCE.toLocaleString()}/year</p>
              )}
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-1.5">
                Annual Return: <span className="text-violet-400 font-medium">{rate}%</span>
              </label>
              <input
                type="range"
                data-testid="input-rate"
                min="1" max="20" step="0.5"
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="w-full accent-violet-500 cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-1.5">
                Years: <span className="text-violet-400 font-medium">{years}</span>
              </label>
              <input
                type="range"
                data-testid="input-years"
                min="1" max="40" step="1"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className="w-full accent-violet-500 cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-1.5">
                Dividend Yield: <span className="text-violet-400 font-medium">{dividendYield}%</span>
              </label>
              <input
                type="range"
                data-testid="input-dividend"
                min="0" max="8" step="0.5"
                value={dividendYield}
                onChange={(e) => setDividendYield(Number(e.target.value))}
                className="w-full accent-violet-500 cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-1.5">Tax Band</label>
              <div className="grid grid-cols-2 gap-2">
                {["basic", "higher"].map((band) => (
                  <button
                    key={band}
                    data-testid={`tax-band-${band}`}
                    onClick={() => setTaxBand(band)}
                    className={`py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                      taxBand === band
                        ? "bg-violet-500 text-white"
                        : "bg-slate-800 text-slate-400 hover:text-white"
                    }`}
                  >
                    {band === "basic" ? "Basic (20%)" : "Higher (40%)"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          <div data-testid="isa-results" className="lg:col-span-3 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-violet-500/5 border border-violet-500/20 rounded-2xl p-5">
                <p className="text-slate-400 text-xs mb-1">ISA Value</p>
                <p data-testid="result-isa" className="font-outfit text-2xl font-bold text-violet-400">{fmt(isaValue)}</p>
                <p className="text-slate-500 text-xs mt-1">Tax-free</p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
                <p className="text-slate-400 text-xs mb-1">GIA Value (After Tax)</p>
                <p data-testid="result-gia" className="font-outfit text-2xl font-bold text-white">{fmt(giaAfterTax)}</p>
                <p className="text-slate-500 text-xs mt-1">After CGT & dividend tax</p>
              </div>
            </div>

            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5">
              <p className="text-slate-400 text-sm mb-1">Tax Saving with ISA</p>
              <p data-testid="result-saving" className="font-outfit text-3xl font-bold text-emerald-400">{fmt(taxSaving)}</p>
              <p className="text-slate-500 text-xs mt-1">estimated tax avoided over {years} years</p>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 space-y-2.5">
              <p className="text-slate-300 text-sm font-medium mb-3">GIA Tax Breakdown</p>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Capital Gains Tax</span>
                <span className="text-red-400 font-mono">{fmt(giaCGT)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Dividend Tax</span>
                <span className="text-red-400 font-mono">{fmt(giaDividendTax)}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-slate-700">
                <span className="text-slate-400">Total Tax Liability</span>
                <span className="text-red-400 font-mono font-semibold">{fmt(giaCGT + giaDividendTax)}</span>
              </div>
            </div>

            <div className="flex items-start gap-2 bg-slate-900/30 border border-slate-800/50 rounded-xl p-4">
              <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-slate-500 text-xs leading-relaxed">
                ISA allowance: £20,000/year. CGT annual exempt amount: £3,000 (2024/25). Figures are illustrative. Not financial advice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
